import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import dayjs from 'dayjs';
import { RemovedCoApplicant } from '@lmc-app/shared/interface/removed-co-applicant.interface';
// Matches RemovedCoApplicantHistory.restored_reason NVARCHAR(500), same as removal_reason
import { REMOVAL_REASON_MAX_LENGTH } from '../remove-co-applicant-dialog/remove-co-applicant-dialog.component';

export interface RestoreCoApplicantDialogData {
	// The application's removals not yet restored
	removals: RemovedCoApplicant[];
}

// "Restore" returns the removal chosen in the dropdown and the reason; "Cancel" and the cross return undefined and change nothing
export type RestoreCoApplicantDialogResult = { removal: RemovedCoApplicant; reason: string } | undefined;

/**
 * Shown on "Restore Co-Applicant": a dropdown of the co-applicants removed from the application
 * that have not been restored, so the dialog stays compact however many removals stack up, and captures why
 */
@Component({
	selector: 'app-restore-co-applicant-dialog',
	templateUrl: './restore-co-applicant-dialog.component.html',
	styleUrls: ['./restore-co-applicant-dialog.component.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatDialogModule,
		MatButtonModule,
		MatFormFieldModule,
		MatIconModule,
		MatInputModule,
		MatSelectModule,
		MatTooltipModule,
	],
})
export class RestoreCoApplicantDialogComponent {
	private readonly dialogRef = inject(MatDialogRef<RestoreCoApplicantDialogComponent, RestoreCoApplicantDialogResult>);
	readonly data = inject<RestoreCoApplicantDialogData>(MAT_DIALOG_DATA);

	// Preselects the most recent removal; the list arrives newest first
	readonly selected = new FormControl<RemovedCoApplicant | null>(this.data.removals[0] ?? null, Validators.required);

	readonly reasonMaxLength = REMOVAL_REASON_MAX_LENGTH;
	readonly reason = new FormControl('', {
		nonNullable: true,
		validators: [Validators.required, Validators.maxLength(REMOVAL_REASON_MAX_LENGTH)],
	});

	formatDateTime(value: string | null | undefined): string {
		return value ? dayjs(value).format('YYYY-MM-DD h:mm A') : '';
	}

	displayName(removal: RemovedCoApplicant | null): string {
		return removal?.coApplicantName?.trim() || 'Unnamed co-applicant';
	}

	trackById(_index: number, removal: RemovedCoApplicant): number {
		return removal.id;
	}

	onRestore(): void {
		const removal = this.selected.value;
		const reason = this.reason.value.trim();
		if (!removal) {
			this.selected.markAsTouched();
			return;
		}
		if (!reason) {
			this.reason.setValue('');
			this.reason.markAsTouched();
			return;
		}
		this.dialogRef.close({ removal, reason });
	}
}
